import React from "react";
import { useTheme } from "react-jss";
import { useSelector } from "react-redux";
import classnames from "classnames";
import { Slider, Rail, Handles, Tracks, Ticks } from "react-compound-slider";
import useStyles from "./SliderRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { LabelPosition } from "../../../models/enums/LabelPosition";
import { RecalledText } from "../../../containers/shared/RecalledText";
import useTranslatable from "../../../hooks/useTranslatable";
import useRecall from "../../../hooks/useRecall";
import useTextDirection from "../../../hooks/useTextDirection";
import Button from "../../shared/Button";
import { isNullOrEmpty } from "../../../utils";
import { Translatable } from "../../../models/Translatable";
import { ElementWithSettings } from "../../../models/ElementWithSettings";
import { ButtonChoice } from "../../shared/ButtonChoice";
import { CheckBoxIcon } from "../../shared/CheckBoxIcon";
import InputWrapper from "../../shared/InputWrapper";
import { shouldSuppressModernHybridGridInputWrapperMargin } from "../../../theme/gridThemeTokens";
import { useModernSliderStyles } from "../../../theme/sliderThemeTokens";
import useQuestion from "../../../hooks/useQuestion";
import { size } from "../../../models/utilities/questionUtilities";
import {
    getAriaLabelledByFromId,
    getHybridGridCellPlainAriaLabel,
    getQuestionTitleAndLabelAriaLabelledByIds,
    stripHtmlForAccessibleName,
} from "../../../utils/WCAGHelper";
import { useHybridGridSubHeaderAria } from "./HybridGridSubHeaderAriaContext";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import { SliderNavigation } from "../../controls/KeyboardNavigation/SliderNavigation";
import { translate } from "../../../models/utilities/translationUtilities";
import { StoreState } from "../../../models/states/StoreState";

interface Props extends RendererProps {
    value: number;
    domain: number[];
    stepValue: number;
    hasValueSet: boolean;
    hasNoAnswer: boolean;
    noAnswerLabel: string;
    decimals?: number;
    displayTicks: boolean;
    tickValues?: number[];
    onChange?(value: number[]): void;
    onClearAnswer?(): void;
    onToggleNoAnswer?(): void;
    displayClearButton: boolean;
    displayNoAnswer?: boolean;
    isDiscrete: boolean;
    discreteValues?: { [value: number]: DiscreteValue };
    stepDecimalPlaces: number;
    tickDecimalPlaces: number;
    defaultPosition?: number;
    hasImages?: boolean;
    isHybridGrid?: boolean;
    elementId: string;
    isDisabled?: boolean;
    /** Hybrid grid: ids on scale anchors for aria-labelledby (same pattern as NetPromoterRenderer). */
    sliderLeftLabelId?: string;
    sliderRightLabelId?: string;
    sliderMiddleLabelId?: string;
}

export interface DiscreteValue extends ElementWithSettings, Translatable {
    elementId: string;
    choiceValueCode: string;
}

function onChoiceKeyDown(event: React.KeyboardEvent, onToggleNoAnswer: () => void) {
    if (event.target !== event.currentTarget) {
        return;
    }
    if (event.key === " ") {
        onToggleNoAnswer();
        event.preventDefault();
    }
}

function Handle({
    handle,
    domain,
    hasValueSet,
    hasNoAnswer,
    getHandleProps,
    isDiscrete,
    discreteValues,
    decimalPlaces,
    defaultPosition,
    isHybridGrid,
    onChange,
    elementReference,
    ariaLabelledBy,
    ariaLabel,
    endpointAccessibleName,
    errorElementId,
    isInvalid,
}: {
    handle: { id: string; value: number; percent: number };
    domain: number[];
    hasValueSet: boolean;
    hasNoAnswer?: boolean;
    getHandleProps: any;
    isDiscrete: boolean;
    discreteValues?: { [value: number]: DiscreteValue };
    decimalPlaces: number;
    defaultPosition?: number;
    isHybridGrid?: boolean;
    onChange?: (value: number[]) => void;
    elementReference: React.RefObject<HTMLDivElement>;
    ariaLabelledBy?: string;
    /** When set (plain hybrid grid name), overrides aria-labelledby on the slider thumb. */
    ariaLabel?: string;
    /**
     * Stripped left/right label text when the value is at the domain min/max. Surfaced via
     * `aria-valuetext` so screen readers announce the endpoint hint as a value change rather
     * than a name change (changing the focused thumb's accessible name on every keypress
     * causes NVDA/JAWS to read the label twice — once for the name update, once for the
     * slider value-change event).
     */
    endpointAccessibleName?: string;
    errorElementId?: string;
    isInvalid?: boolean;
}) {
    const { id, value, percent } = handle;
    const [active, setActive] = React.useState(false);
    const [min, max] = domain;

    const theme = useTheme();
    const classes = useStyles({
        theme,
        percent: hasValueSet ? percent : defaultPosition,
        isHybridGrid,
    } as any);
    const isModernSlider = useModernSliderStyles(theme as any);
    // Discrete sliders surface the human-readable choice via aria-valuetext so screen readers
    // read "Strongly Agree" instead of just the numeric position. For continuous sliders we
    // only emit aria-valuetext at the domain endpoints (with the left/right label appended)
    // so reaching min/max announces the endpoint name without altering the thumb's name.
    const discreteText = useTranslatable(discreteValues?.[value] as any, "TEXT");
    const discreteValueText =
        stripHtmlForAccessibleName(discreteText || "") ||
        discreteValues?.[value]?.choiceValueCode ||
        undefined;
    const valueText = isDiscrete
        ? discreteValueText
        : endpointAccessibleName
          ? `${value.toFixed(decimalPlaces)}, ${endpointAccessibleName}`
          : undefined;
    // ARIA spec requires aria-valuenow to be present from first focus when the value is known,
    // so we fall back to defaultPosition before the user has interacted. After No Answer is
    // selected the value is explicitly "unknown" — omit the attribute. Format with decimals so
    // the screen reader hears the same value the visible handle label shows.
    const numericValueNow = hasNoAnswer
        ? undefined
        : hasValueSet
          ? value.toFixed(decimalPlaces)
          : (defaultPosition ?? min).toFixed(decimalPlaces);
    const onFocus = () => {
        if (!hasValueSet) {
            onChange?.([value]);
        }
        setActive(true);
    };

    return (
        <div
            className={classnames(classes.handle, active && classes.handleActive)}
            {...getHandleProps(id)}
            tabIndex={0}
            role="slider"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={numericValueNow}
            aria-valuetext={valueText}
            aria-label={ariaLabel || undefined}
            aria-labelledby={ariaLabel ? undefined : ariaLabelledBy || undefined}
            aria-describedby={errorElementId}
            aria-invalid={isInvalid || undefined}
            onBlur={() => setActive(false)}
            onMouseOut={() => setActive(false)}
            ref={elementReference}
        >
            {hasValueSet && (
                <div
                    onFocus={onFocus}
                    onMouseOver={() => setActive(true)}
                    className={classnames(
                        classes.handleLabelContainer,
                        isModernSlider
                            ? classes.handleLabelContainerModern
                            : isHybridGrid && !isDiscrete
                              ? classes.handleLabelContainerHybridGrid
                              : classes.handleLabelContainerRegular,
                    )}
                >
                    <HandleLabel
                        discreteValues={discreteValues}
                        value={value}
                        isHybridGrid={isHybridGrid}
                        decimalPlaces={decimalPlaces}
                        isDiscrete={isDiscrete}
                    />
                </div>
            )}
        </div>
    );
}

function HandleLabel({
    discreteValues,
    value,
    isHybridGrid,
    decimalPlaces,
    isDiscrete,
}: {
    discreteValues?: { [value: number]: DiscreteValue };
    value: number;
    isHybridGrid?: boolean;
    decimalPlaces: number;
    isDiscrete: boolean;
}) {
    const imageUrl = useTranslatable(discreteValues?.[value] as any, "ImageUrl");
    if (!isNullOrEmpty(imageUrl)) {
        return (
            <DiscreteHandleLabelWithImage
                value={discreteValues?.[value]!}
                isHybridGrid={isHybridGrid}
            />
        );
    }
    if (isDiscrete) {
        return <DiscreteHandleLabel value={discreteValues?.[value]!} isHybridGrid={isHybridGrid} />;
    }
    return (
        <StandardHandleLabel
            value={value}
            decimalPlaces={decimalPlaces}
            isHybridGrid={isHybridGrid}
        />
    );
}

function StandardHandleLabel({
    value,
    decimalPlaces,
    isHybridGrid,
}: {
    value: number;
    decimalPlaces: number;
    isHybridGrid?: boolean;
}) {
    const theme = useTheme();
    const classes = useStyles({ theme } as any);
    const displayValue = value.toFixed(decimalPlaces);
    const isModernSlider = useModernSliderStyles(theme as any);

    if (isModernSlider) {
        return (
            <div className={classnames(classes.handleLabelText, classes.handleLabelTextModern)}>
                {displayValue}
            </div>
        );
    }

    return (
        <>
            <div className={classes.handleLabelBackground} />
            {!isHybridGrid && <HandleLabelArrow />}
            <div
                className={classnames(
                    classes.handleLabelText,
                    isHybridGrid
                        ? classes.handleLabelTextHybridGrid
                        : classes.handleLabelTextRegular,
                )}
            >
                {displayValue}
            </div>
            {displayValue} {/* This is a placeholder to calculate the container size */}
        </>
    );
}

function DiscreteHandleLabel({
    value,
    isHybridGrid,
}: {
    value: DiscreteValue;
    isHybridGrid?: boolean;
}) {
    const alt = useTranslatable(value, "TEXT") || value.choiceValueCode;
    const theme = useTheme();
    const classes = useStyles({ theme, isDiscrete: true } as any);
    const isModernSlider = useModernSliderStyles(theme as any);

    if (isModernSlider) {
        return (
            <div
                className={classnames(classes.handleLabelText, classes.handleLabelTextModern)}
                title={alt}
            >
                {alt}
            </div>
        );
    }

    return (
        <>
            <div className={classes.handleLabelBackground} />
            {!isHybridGrid && <HandleLabelArrow isDiscrete />}
            <div
                className={classnames(classes.handleLabelText, classes.handleLabelTextRegular)}
                title={alt}
            >
                <div className={classes.handleLabelTextDiscrete}>{alt}</div>
            </div>
            {alt}
        </>
    );
}

function DiscreteHandleLabelWithImage({
    value,
    isHybridGrid,
}: {
    value: DiscreteValue;
    isHybridGrid?: boolean;
}) {
    const imageUrl = useTranslatable(value, "ImageUrl");
    const alt = useTranslatable(value, "TEXT") || value.choiceValueCode;
    const theme = useTheme();
    const classes = useStyles({ theme, imageUrl, isDiscrete: true } as any);
    const isModernSlider = useModernSliderStyles(theme as any);

    if (isModernSlider) {
        return (
            <div className={classes.handleLabelImageContainer} title={alt}>
                <div className={classes.handleLabelImage} />
                <div className={classnames(classes.handleLabelText, classes.handleLabelTextModern)}>
                    {alt}
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={classes.handleLabelBackground} />
            {!isHybridGrid && <HandleLabelArrow isDiscrete />}
            <div
                className={classnames(
                    classes.handleLabelImageContainer,
                    classes.handleLabelTextRegular,
                )}
                title={alt}
            >
                <div className={classes.handleLabelImage} />
                <div className={classes.handleLabelTextDiscrete}>{alt}</div>
            </div>
            {alt.length < 10 ? (
                <div>{alt}</div>
            ) : (
                <div>{alt.concat(alt).substring(0, (alt.length * 2) / 1.6)}</div>
            )}
        </>
    );
}

function HandleLabelArrow({ isDiscrete }: { isDiscrete?: boolean }) {
    const theme = useTheme();
    const classes = useStyles({ theme, isDiscrete } as any);
    return (
        <>
            <div className={classes.handleLabelArrowBorder} />
            <div className={classes.handleLabelArrow} />
        </>
    );
}

function Tick({
    tick,
    onChange,
    count,
    isDiscrete,
    discreteValues,
    decimalPlaces,
    isHybridGrid,
}: {
    tick: { percent: number; value: number };
    onChange: (value: number[]) => void;
    count: number;
    isDiscrete: boolean;
    discreteValues?: { [value: number]: DiscreteValue };
    decimalPlaces: number;
    isHybridGrid?: boolean;
}) {
    const { percent, value } = tick;
    const theme = useTheme();
    const classes = useStyles({ theme, percent, count } as any);

    const id = isDiscrete ? discreteValues?.[value]?.elementId : null;
    const discreteValue = isDiscrete ? discreteValues?.[value] : undefined;
    const discreteValueText =
        useTranslatable(discreteValue as any, "TEXT") ||
        (discreteValue !== undefined && discreteValue.choiceValueCode) ||
        value;
    const displayValue = isDiscrete ? discreteValueText : value.toFixed(decimalPlaces);

    return (
        <>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
            <div onClick={() => onChange([value])}>
                <div className={classes.tick} />
                <div
                    id={id || undefined}
                    className={classnames(
                        classes.tickLabel,
                        isHybridGrid ? classes.tickLabelHybridGrid : classes.tickLabelRegular,
                    )}
                    dangerouslySetInnerHTML={{ __html: String(displayValue || "") }}
                />
            </div>
        </>
    );
}

function sliderValueMatchesEndpoint(
    value: number,
    endpoint: number,
    decimalPlaces: number,
): boolean {
    if (Number.isNaN(value) || Number.isNaN(endpoint)) {
        return false;
    }
    return value.toFixed(decimalPlaces) === endpoint.toFixed(decimalPlaces);
}

function Label({
    text,
    position,
    htmlId,
}: {
    text: string;
    position: number;
    /** Optional DOM id for aria-labelledby (e.g. hybrid grid middle label on thumb focus). */
    htmlId?: string;
}) {
    const theme = useTheme();
    const classes = useStyles({ theme } as any);
    const positionalClasses: { [key: number]: string } = {
        [LabelPosition.Left]: classes.labelLeft,
        [LabelPosition.Middle]: classes.labelMiddle,
        [LabelPosition.Right]: classes.labelRight,
    };
    if (isNullOrEmpty(text)) {
        return null;
    }
    return (
        <RecalledText
            text={text}
            render={(recalled) => (
                <div
                    id={htmlId}
                    className={classnames(classes.label, positionalClasses[position])}
                    dangerouslySetInnerHTML={{ __html: recalled }}
                />
            )}
        />
    );
}

function ClearButton({
    theme,
    question,
    onClearAnswer,
    isDisabled = false,
}: {
    theme: any;
    question: any;
    onClearAnswer: () => void;
    isDisabled?: boolean;
}) {
    const jssTheme = useTheme();
    const classes = useStyles({ theme: jssTheme } as any);
    const isModernSlider = useModernSliderStyles(jssTheme as any);
    const defaultText = useTranslatable(theme, "QuestionClearButton") || "Reset";
    const text = useTranslatable(question, "ResetButtonText") || defaultText || "Reset";

    return (
        <Button
            className={classnames(
                "slider__button--clear",
                isModernSlider && classes.clearButtonModern,
                isDisabled && "disabled",
            )}
            text={text}
            size={isModernSlider ? "small" : "full"}
            outlineAccent={isModernSlider ? "secondary" : undefined}
            onClick={() => onClearAnswer()}
        />
    );
}

function CompoundSlider(props: Props) {
    const {
        value,
        domain,
        hasValueSet,
        hasNoAnswer,
        stepValue,
        onChange,
        displayTicks,
        tickValues,
        isDiscrete,
        discreteValues,
        stepDecimalPlaces,
        tickDecimalPlaces,
        defaultPosition,
        hasImages,
        isHybridGrid,
        elementId,
        isDisabled,
        gridRenderedRowIndex,
        gridRenderedColumnIndex,
        language,
        questionnaire,
    } = props;
    const theme = useTheme();
    const question = useQuestion();
    const dir = useTextDirection();
    const scaleLeftLabel = useTranslatable(question, "LeftLabel");
    const scaleMiddleLabel = useTranslatable(question, "MiddleLabel");
    const scaleRightLabel = useTranslatable(question, "RightLabel");
    const questionId = question?.parent?.elementId ?? question?.elementId;
    const questionType = question?.parent?.type ?? question?.type;
    const includeHybridGridSubHeaderChrome = useHybridGridSubHeaderAria();
    const [variable] = question?.variables ?? [];

    const sliderSize = isHybridGrid ? size(question) : "Full";
    const classes = useStyles({ theme, size: sliderSize, hasImages, isHybridGrid, displayTicks } as any);
    const [isSliderActive, setActivateSlider] = React.useState(false);

    const ariaAttributes: { [key: string]: string } = {};
    const { elementReference, onKeyDown } = useKeyboardNavigation(
        elementId,
        variable,
        question,
        SliderNavigation,
        null,
    );
    const handlerReference = React.createRef<HTMLDivElement>();
    function scroll(elementToFocus: HTMLElement) {
        const rect = elementToFocus.getBoundingClientRect();
        const isInViewport =
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth);

        if (!isInViewport) {
            elementToFocus.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    function scrollIntoViewAndSetFocus(elementToFocus: HTMLElement, event: React.KeyboardEvent) {
        if (typeof elementToFocus.scrollIntoView === "function") {
            scroll(elementToFocus);
        }
        elementToFocus.focus();
        event.preventDefault();
    }

    function onKeydownSlider(event: React.KeyboardEvent) {
        if (event.key === " ") {
            if (isSliderActive) {
                setActivateSlider(false);
                scrollIntoViewAndSetFocus(elementReference.current!, event);
            } else {
                setActivateSlider(true);
                scrollIntoViewAndSetFocus(handlerReference.current!, event);
            }
        } else if (event.key === "Tab") {
            setActivateSlider(false);
            onKeyDown(event);
        } else if (
            event.key === "ArrowLeft" ||
            event.key === "ArrowRight" ||
            event.key === "ArrowUp" ||
            event.key === "ArrowDown"
        ) {
            onKeyDown(event);
        }
    }

    function getSliderDisabledCondition(): boolean {
        if (isHybridGrid) {
            if (isDisabled === true) {
                return true;
            }
            if (isSliderActive === false) {
                return true;
            }
            return false;
        }

        return isDisabled === true;
    }

    const hybridPlainAccessibleName =
        isHybridGrid && language
            ? getHybridGridCellPlainAriaLabel(
                  question,
                  language,
                  gridRenderedColumnIndex ?? 0,
                  gridRenderedRowIndex ?? 0,
                  includeHybridGridSubHeaderChrome,
              )
            : "";

    const { sliderLeftLabelId, sliderRightLabelId, sliderMiddleLabelId } = props;
    const [domainMin, domainMax] = domain;

    const middleStripForGridChrome =
        sliderMiddleLabelId !== undefined ? stripHtmlForAccessibleName(scaleMiddleLabel ?? "") : "";

    if (isHybridGrid) {
        if (hybridPlainAccessibleName) {
            const trackParts = [hybridPlainAccessibleName];
            if (middleStripForGridChrome) {
                trackParts.push(middleStripForGridChrome);
            }
            ariaAttributes["aria-label"] = trackParts.join(", ");
        } else {
            const headerIds = getAriaLabelledByFromId(
                question,
                questionId,
                `${questionType}_SLIDER`,
                (gridRenderedColumnIndex || 0) + 1,
                (gridRenderedRowIndex || 0) + 1,
                undefined,
                undefined,
                undefined,
                includeHybridGridSubHeaderChrome,
            );
            ariaAttributes["aria-labelledby"] = sliderMiddleLabelId
                ? `${headerIds} ${sliderMiddleLabelId}`.trim()
                : headerIds;
        }
    }

    const atMin =
        !hasNoAnswer &&
        sliderLeftLabelId !== undefined &&
        sliderValueMatchesEndpoint(value, domainMin, stepDecimalPlaces);
    const atMax =
        !hasNoAnswer &&
        sliderRightLabelId !== undefined &&
        sliderValueMatchesEndpoint(value, domainMax, stepDecimalPlaces);

    // Endpoint hint goes through `aria-valuetext` on the thumb (see `Handle`). Driving the
    // thumb's accessible *name* (aria-labelledby / aria-label) from the live value caused
    // screen readers to announce the endpoint label twice when the user moved into min/max
    // — once for the name change and again for the value change. `aria-valuetext` only
    // fires the slider value-change announcement, so the endpoint is read exactly once.
    const endpointAccessibleName = isHybridGrid
        ? atMin
            ? stripHtmlForAccessibleName(scaleLeftLabel ?? "") || undefined
            : atMax
              ? stripHtmlForAccessibleName(scaleRightLabel ?? "") || undefined
              : undefined
        : undefined;

    // Outer `.slider__container` carries grid/column/middle context for hybrid; the thumb
    // intentionally has no extra accessible name in hybrid mode so it stays stable across
    // value changes. Non-hybrid keeps its question title/label labelledby.
    let handleAriaLabel: string | undefined;
    let handleAriaLabelledBy: string | undefined;

    if (!isHybridGrid) {
        handleAriaLabelledBy = getQuestionTitleAndLabelAriaLabelledByIds(question, language);
    }

    const isInvalid = useSelector((state: StoreState) => {
        const name = variable?.name;
        if (!name) {
            return false;
        }
        const errs = state.interview.validation?.errorsByVariableName?.[name];
        return (errs?.length ?? 0) > 0;
    });
    // QuestionRenderer renders the error message with id `${question.elementId}_ERROR` at the
    // top question level — for HybridGrid cells that's the parent grid question.
    const errorElementId = isInvalid && questionId ? `${questionId}_ERROR` : undefined;

    return (
        <div
            id={elementId}
            tabIndex={isHybridGrid ? 0 : -1}
            role="button"
            ref={isHybridGrid ? elementReference : null}
            onKeyDown={(event) => (isHybridGrid ? onKeydownSlider(event) : undefined)}
            className={`${classes.sliderContainer} ${isDisabled && classes.disabled}`}
            {...ariaAttributes}
            onMouseEnter={() => setActivateSlider(true)}
            onTouchStart={() => setActivateSlider(true)}
        >
            <Slider
                className={classes.slider}
                mode={1}
                domain={domain}
                step={stepValue}
                values={[value]}
                reversed={dir === "rtl"}
                onUpdate={
                    value || isDisabled
                        ? undefined
                        : (values: readonly number[]) => onChange?.(values as number[])
                }
                onChange={
                    isDisabled
                        ? undefined
                        : (values: readonly number[]) => onChange?.(values as number[])
                }
                warnOnChanges={false}
                disabled={getSliderDisabledCondition()}
            >
                <Rail>
                    {({ getRailProps }) => (
                        <div
                            className={`${classes.rail} ${isDisabled && classes.disabled}`}
                            {...getRailProps()}
                        />
                    )}
                </Rail>
                {!isDiscrete && (
                    <Tracks right={false}>
                        {({ tracks, getTrackProps }) => (
                            <div className={classes.trackContainer}>
                                {tracks.map(({ id, source, target }) => (
                                    <div
                                        key={id}
                                        className={classnames(
                                            classes.track,
                                            isDisabled && classes.disabled,
                                        )}
                                        style={{
                                            left: `${source.percent}%`,
                                            width: `${target.percent - source.percent}%`,
                                        }}
                                        {...getTrackProps()}
                                    />
                                ))}
                            </div>
                        )}
                    </Tracks>
                )}
                <Handles>
                    {({ handles, getHandleProps }) => (
                        <div className={classes.handleContainer}>
                            {handles.map((handle) => (
                                <Handle
                                    key={handle.id}
                                    handle={handle}
                                    hasValueSet={hasValueSet}
                                    hasNoAnswer={hasNoAnswer}
                                    getHandleProps={getHandleProps}
                                    isDiscrete={isDiscrete}
                                    discreteValues={discreteValues}
                                    decimalPlaces={stepDecimalPlaces}
                                    isHybridGrid={isHybridGrid}
                                    domain={domain}
                                    defaultPosition={defaultPosition}
                                    onChange={isDisabled ? undefined : onChange}
                                    elementReference={
                                        isHybridGrid ? handlerReference : elementReference
                                    }
                                    ariaLabelledBy={handleAriaLabelledBy}
                                    ariaLabel={handleAriaLabel}
                                    endpointAccessibleName={endpointAccessibleName}
                                    errorElementId={errorElementId}
                                    isInvalid={isInvalid}
                                />
                            ))}
                        </div>
                    )}
                </Handles>
                {displayTicks && (
                    <Ticks values={tickValues}>
                        {({ ticks }) => (
                            <div className={classes.tickContainer}>
                                {ticks.map((tick) => (
                                    <Tick
                                        key={tick.id}
                                        tick={tick}
                                        count={ticks.length}
                                        isDiscrete={isDiscrete}
                                        onChange={onChange!}
                                        discreteValues={discreteValues}
                                        decimalPlaces={tickDecimalPlaces}
                                        isHybridGrid={isHybridGrid}
                                    />
                                ))}
                            </div>
                        )}
                    </Ticks>
                )}
            </Slider>
        </div>
    );
}

function NoAnswer(props: Props) {
    const {
        displayNoAnswer,
        hasNoAnswer,
        onToggleNoAnswer,
        isHybridGrid,
        noAnswerLabel,
        isDisabled = false,
    } = props;
    const theme = useTheme();
    const classes = useStyles({ theme } as any);
    const recalledLabel = useRecall(noAnswerLabel);

    if (!displayNoAnswer) {
        return null;
    }

    if (isHybridGrid) {
        return (
            <div
                role="checkbox"
                aria-checked={hasNoAnswer}
                tabIndex={0}
                className={classes.noAnswer}
                onKeyDown={
                    isDisabled ? undefined : (event) => onChoiceKeyDown(event, onToggleNoAnswer!)
                }
                onClick={isDisabled ? undefined : onToggleNoAnswer!}
            >
                <CheckBoxIcon checked={hasNoAnswer} backgroundless disabled={isDisabled} />
                <span className={classes.noAnswerLabel}>
                    <RecalledText text={noAnswerLabel} />
                </span>
            </div>
        );
    }

    return (
        <ButtonChoice
            type="checkbox"
            onKeyDown={
                isDisabled ? undefined : (event) => onChoiceKeyDown(event, onToggleNoAnswer!)
            }
            onSelect={isDisabled ? undefined : onToggleNoAnswer!}
            selected={hasNoAnswer}
            label={recalledLabel}
            className={classes.noAnswer}
        />
    );
}

export default function SliderRenderer(props: Props) {
    const {
        theme: surveyTheme,
        question,
        displayClearButton,
        onClearAnswer,
        isHybridGrid,
        isDisabled,
        questionnaire,
        language,
    } = props;
    const theme = useTheme();
    const classes = useStyles({ theme } as any);

    const leftLabel = useTranslatable(question, "LeftLabel");
    const middleLabel = useTranslatable(question, "MiddleLabel");
    const rightLabel = useTranslatable(question, "RightLabel");
    const questionElementId = question?.elementId;
    const hasSliderLeftLabelText = !isNullOrEmpty(stripHtmlForAccessibleName(leftLabel ?? ""));
    const hasSliderRightLabelText = !isNullOrEmpty(stripHtmlForAccessibleName(rightLabel ?? ""));
    const hasSliderMiddleLabelText = !isNullOrEmpty(stripHtmlForAccessibleName(middleLabel ?? ""));
    const sliderLeftLabelId =
        isHybridGrid && questionElementId && hasSliderLeftLabelText
            ? `${questionElementId}_SLIDER_LEFTLABEL`
            : undefined;
    const sliderRightLabelId =
        isHybridGrid && questionElementId && hasSliderRightLabelText
            ? `${questionElementId}_SLIDER_RIGHTLABEL`
            : undefined;
    const sliderMiddleLabelId =
        isHybridGrid && questionElementId && hasSliderMiddleLabelText
            ? `${questionElementId}_SLIDER_MIDDLELABEL`
            : undefined;

    const valueResetAnnouncement =
        translate(questionnaire, "SliderValueReset", language) || "Value reset";
    const [statusAnnouncement, setStatusAnnouncement] = React.useState("");

    // role="slider" already announces aria-valuenow changes natively, so the live region only
    // covers actions that aren't part of the slider's own value (currently just Reset/Clear).
    const handleClearAnswer = () => {
        if (isDisabled) {
            return;
        }
        onClearAnswer?.();
        setStatusAnnouncement(valueResetAnnouncement);
    };

    const slider = (
        <>
            <CompoundSlider
                {...props}
                sliderLeftLabelId={sliderLeftLabelId}
                sliderRightLabelId={sliderRightLabelId}
                sliderMiddleLabelId={sliderMiddleLabelId}
            />
            <div className={`${classes.labelContainer} ${isDisabled && classes.disabled}`}>
                {!isHybridGrid && <Label text={leftLabel} position={LabelPosition.Left} />}
                <Label
                    text={middleLabel}
                    position={LabelPosition.Middle}
                    htmlId={sliderMiddleLabelId}
                />
                {!isHybridGrid && <Label text={rightLabel} position={LabelPosition.Right} />}
            </div>
            <NoAnswer {...props} />
            {displayClearButton && (
                <ClearButton
                    theme={surveyTheme}
                    question={question}
                    onClearAnswer={handleClearAnswer}
                    isDisabled={isDisabled}
                />
            )}
            <span className={classes.srOnly} role="status" aria-live="polite" aria-atomic="true">
                {statusAnnouncement}
            </span>
        </>
    );

    if (isHybridGrid)
        return (
            <div className={classes.hybridSliderWrapper}>
                <InputWrapper
                    leftLabel={leftLabel}
                    rightLabel={rightLabel}
                    leftLabelId={sliderLeftLabelId}
                    rightLabelId={sliderRightLabelId}
                    suppressTopMargin={shouldSuppressModernHybridGridInputWrapperMargin(
                        theme,
                        isHybridGrid,
                    )}
                >
                    {slider}
                </InputWrapper>
            </div>
        );

    return slider;
}
